import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { isEmailNotConfirmed } from '../lib/authErrors';
import ResendVerification from './auth/ResendVerification';

/**
 * SignUpGate
 * Overlay modal with real Supabase auth. No close button — user must sign up/in.
 * Props: gate — object returned by useGuestGate()
 */
export default function SignUpGate({ gate }) {
  const [mode, setMode]               = useState('signup');
  const [name, setName]               = useState('');
  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]         = useState(false);
  const [oauthLoading, setOauthLoading] = useState('');
  const [error, setError]             = useState('');
  const [notConfirmed, setNotConfirmed] = useState(false); // sign-in refused: email never confirmed
  const [confirmed, setConfirmed]     = useState(false); // email sent state

  if (!gate.gateVisible) return null;

  // Tropes are optional on Reanlo, never the headline -- see "COPY RULES" in
  // CLAUDE.md for the approved discovery wording.
  const copy = {
    homepage:  { headline: 'Reading beyond the algorithm', sub: 'Conversation-powered book discovery. Create your free account to keep going.' },
    profile:   { headline: 'Your bookish ecosystem',       sub: 'Starting with the shareable home for your bookish identity. Create your free account to save it.' },
    community: { headline: 'Gossip, gems & hot takes',     sub: "Join in on the conversation and see what everyone's reading, asking & arguing about." },
  };
  const { headline, sub } = copy[gate.gateContext] ?? copy.homepage;

  // Assign reader role — safe to call multiple times, only works with active session
  async function assignReaderRole(userId) {
    try {
      await supabase
        .from('user_roles')
        .upsert(
          { user_id: userId, role: 'reader' },
          { onConflict: 'user_id,role', ignoreDuplicates: true }
        );
    } catch (err) {
      // Non-fatal — DB trigger will handle this if it fails
      console.warn('assignReaderRole skipped:', err.message);
    }
  }

  async function handleOAuth(provider) {
    setOauthLoading(provider);
    setError('');
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/`,
        ...(provider === 'apple' && { queryParams: { response_mode: 'form_post' } }),
      },
    });
    if (error) { setError(error.message); setOauthLoading(''); }
    // Reader role for OAuth handled in app-level onAuthStateChange
  }

  async function handleEmailSubmit() {
    setError('');
    setNotConfirmed(false);
    if (!email || !password) { setError('Please fill in all fields.'); return; }
    if (mode === 'signup' && password.length < 8) { setError('Password must be at least 8 characters.'); return; }

    setLoading(true);

    if (mode === 'signup') {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { display_name: name || email.split('@')[0] },
          // Matches the OAuth branch above: back to the origin they started
          // from, not to production. See useAuth.js signUp for the same fix.
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) {
        setError(error.message);
        setLoading(false);
      } else {
        // data.session is null when email confirmation is required.
        // Only assign role if we have an active session right now.
        // If confirmation is on, the DB trigger handles role assignment after confirmation.
        if (data?.session && data?.user) {
          await assignReaderRole(data.user.id);
          // onAuthStateChange fires → hook closes the gate automatically
        } else {
          // Email confirmation required — stop the spinner, show check-email message
          setLoading(false);
          setConfirmed(true);
        }
      }
    } else {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        // Same as LoginPage: say what Supabase's wording means and offer the
        // email again.
        if (isEmailNotConfirmed(error)) {
          setNotConfirmed(true);
          setError("This email hasn't been confirmed yet. Click the link in the email we sent you, or send it again.");
        } else {
          setError(error.message);
        }
        setLoading(false);
      } else if (data?.user) {
        // Active session always exists on login — safe to assign role
        await assignReaderRole(data.user.id);
        // onAuthStateChange fires → hook closes the gate automatically
      }
    }
  }

  const anyLoading = loading || !!oauthLoading;

  // ── Email confirmation sent state ─────────────────────────────────────────
  if (confirmed) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,14,13,0.72)', backdropFilter: 'blur(8px)' }} />
        <div style={{ position: 'relative', width: 400, background: 'var(--brand-white)', borderRadius: 24, overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.3)', padding: '40px 32px', textAlign: 'center' }}>
          <div style={{ fontSize: 'var(--font-size-hero)', marginBottom: 'var(--space-lg)' }}>📬</div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 10px' }}>Check your email</h2>
          <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 20px', lineHeight: 1.6 }}>
            We sent a confirmation link to <strong>{email}</strong>. Click it to activate your account and you'll be taken straight back here.
          </p>
          <div style={{ textAlign: 'center' }}>
            <ResendVerification key={email.trim()} email={email.trim()} />
          </div>
          <button
            onClick={() => { setConfirmed(false); setMode('login'); setPassword(''); }}
            style={{ padding: '10px 24px', background: 'var(--brand-rust)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: 'pointer' }}>
            I've confirmed, sign me in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,14,13,0.72)', backdropFilter: 'blur(8px)' }} />

      <div style={{ position: 'relative', width: 400, background: 'var(--brand-white)', borderRadius: 24, overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.3)', maxHeight: '92vh', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, var(--brand-cream), var(--feedback-warning-bg))', padding: '24px 28px 20px', position: 'relative', overflow: 'hidden', borderBottom: '1px solid var(--feedback-warning-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-14)' }}>
            <div>
              <img src="/logo.png" alt="Reanlo" style={{ width: 24, height: 24, objectFit: 'contain' }} />
            </div>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: 0.5 }}>reanlo</span>
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px', lineHeight: 1.2 }}>{headline}</h2>
          <p style={{ fontSize: 'var(--font-size-sm)', color: '#8b7355', margin: 'var(--space-none)', lineHeight: 1.5 }}>{sub}</p>
          <div style={{ position: 'absolute', right: 16, bottom: -6, display: 'flex', gap: 3, opacity: 0.15, pointerEvents: 'none' }}>
            {['#B85C38','#d4a84b','#2D4A3E','#7c3aed','#be185d'].map((c, i) => (
              <div key={i} style={{ width: 10, height: 32 + (i % 3) * 6, background: c, borderRadius: '2px 3px 3px 2px', transform: `rotate(${[-2,1,-1,2,-1][i]}deg)` }} />
            ))}
          </div>
        </div>

        {/* Form */}
        <div style={{ padding: '20px 28px 24px' }}>

          {/* Toggle */}
          <div style={{ display: 'flex', background: 'var(--neutral-100)', borderRadius: 'var(--radius-10)', padding: 3, marginBottom: 'var(--space-lg)' }}>
            {['signup', 'login'].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(''); setNotConfirmed(false); }}
                style={{ flex: 1, padding: '7px', borderRadius: 'var(--radius-md)', border: 'none', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', background: mode === m ? 'var(--brand-white)' : 'transparent', color: mode === m ? 'var(--neutral-800)' : 'var(--neutral-500)', boxShadow: mode === m ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.15s' }}>
                {m === 'signup' ? 'Create account' : 'Sign in'}
              </button>
            ))}
          </div>

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--radius-10)', padding: '9px 13px', marginBottom: 'var(--space-md)' }}>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-error)', margin: 'var(--space-none)' }}>{error}</p>
            </div>
          )}
          {notConfirmed && <ResendVerification key={email.trim()} email={email.trim()} />}

          {/* OAuth */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', marginBottom: 'var(--space-14)' }}>
            {[
              { provider: 'google', label: 'Continue with Google',    bg: '#fff', border: '1px solid var(--brand-sand)', color: 'var(--neutral-800)', icon: <GoogleIcon /> },
              { provider: 'apple',  label: 'Continue with Apple',     bg: '#000', border: 'none',               color: 'var(--brand-white)',    icon: <AppleIcon /> },
            ].map(({ provider, label, bg, border, color, icon }) => (
              <button key={provider} onClick={() => handleOAuth(provider)} disabled={anyLoading}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', width: '100%', padding: '10px 16px', background: bg, border, borderRadius: 'var(--radius-10)', cursor: anyLoading ? 'not-allowed' : 'pointer', fontSize: 'var(--font-size-base)', fontWeight: 600, color, opacity: oauthLoading && oauthLoading !== provider ? 0.5 : 1 }}>
                {icon}
                <span>{oauthLoading === provider ? 'Connecting...' : label}</span>
              </button>
            ))}
          </div>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-14)' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--brand-sand)' }} />
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>or</span>
            <div style={{ flex: 1, height: 1, background: 'var(--brand-sand)' }} />
          </div>

          {/* Email fields */}
          {mode === 'signup' && (
            <input placeholder="Display name" value={name} onChange={e => setName(e.target.value)} disabled={anyLoading} style={inputStyle} />
          )}
          <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} disabled={anyLoading} style={{ ...inputStyle, marginTop: 'var(--space-sm)' }} />
          <div style={{ position: 'relative', marginTop: 'var(--space-sm)', marginBottom: 'var(--space-14)' }}>
            <input type={showPassword ? 'text' : 'password'} placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} disabled={anyLoading} style={{ ...inputStyle, paddingRight: 44 }} />
            <button onClick={() => setShowPassword(p => !p)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)' }}>
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>

          {mode === 'login' && (
            <p style={{ fontSize: 'var(--font-size-xs)', margin: '-6px 0 10px', textAlign: 'right' }}>
              <Link to="/forgot-password" style={{ color: 'var(--brand-rust)', textDecoration: 'none' }}>Forgot password?</Link>
            </p>
          )}

          <button onClick={handleEmailSubmit} disabled={anyLoading}
            style={{ width: '100%', padding: 'var(--space-md)', background: anyLoading ? 'var(--neutral-400)' : 'var(--brand-rust)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: anyLoading ? 'not-allowed' : 'pointer', marginBottom: 'var(--space-10)' }}>
            {loading ? (mode === 'signup' ? 'Creating account...' : 'Signing in...') : (mode === 'signup' ? 'Create my account' : 'Sign in')}
          </button>

          {mode === 'signup' && (
            <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', textAlign: 'center', margin: 'var(--space-none)', lineHeight: 1.5 }}>
              By creating an account you agree to our{' '}
              <a href="/terms" target="_blank" style={{ color: 'var(--brand-rust)' }}>Terms of Service</a>{' '}and{' '}
              <a href="/privacy" target="_blank" style={{ color: 'var(--brand-rust)' }}>Privacy Policy</a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '10px 14px', border: '1px solid var(--brand-sand)',
  borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box', display: 'block',
};

function GoogleIcon() {
  return (<svg width="18" height="18" viewBox="0 0 24 24" style={{ flexShrink: 0 }}><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>);
}
function AppleIcon() {
  return (<svg width="18" height="18" viewBox="0 0 24 24" fill="white" style={{ flexShrink: 0 }}><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>);
}